import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Chip,
  Stack,
  alpha
} from '@mui/material';
import { api } from '@/services/api';
import type { ApplicationResponse } from '@/types/models';
import { ROUTES } from '@/config/routes';

export const ApplicationsPage: React.FC = () => {
  const navigate = useNavigate();
  
  const { data: applications, isLoading, error } = useQuery<ApplicationResponse[]>({
    queryKey: ['applications'],
    queryFn: async () => {
      try {
        return await api.applications.getApplications();
      } catch (err) {
        console.error('ApplicationsPage - Error fetching applications:', err);
        throw err;
      }
    }
  });

  const handleApplicationClick = (applicationId: number) => {
    navigate(ROUTES.STUDENT.APPLICATIONS.DETAIL(applicationId));
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load applications. Please try again later.
      </Alert>
    );
  }

  if (!applications || applications.length === 0) {
    return (
      <Alert severity="info">
        You haven't submitted any applications yet.
      </Alert>
    );
  }

  return (
    <>
      <Box mb={6}>
        <Typography variant="h4" gutterBottom fontWeight="medium">
          My Applications
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Track the status of your volunteer applications
        </Typography>
      </Box>

      <Stack spacing={3}>
        {applications.map((application) => (
          <Paper
            key={application.id}
            elevation={0}
            onClick={() => handleApplicationClick(application.id)}
            sx={{
              p: 3,
              borderRadius: 4,
              bgcolor: theme => alpha(theme.palette.background.paper, 0.8),
              cursor: 'pointer',
              transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 4,
              }
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Box>
                <Typography variant="h6" gutterBottom>
                  {application.opportunity.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {application.opportunity.organization.name}
                </Typography>
              </Box>
              <Chip
                label={application.status}
                color={application.status.toLowerCase() === 'approved' ? 'success' : 
                       application.status.toLowerCase() === 'pending' ? 'warning' : 'error'}
                sx={{ textTransform: 'capitalize' }}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 4, color: 'text.secondary' }}>
              <Box>
                <Typography variant="caption" display="block" color="text.secondary" gutterBottom>
                  Applied On
                </Typography>
                <Typography variant="body2">
                  {new Date(application.created_at).toLocaleDateString()}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" display="block" color="text.secondary" gutterBottom>
                  Location
                </Typography>
                <Typography variant="body2">
                  {application.opportunity.location_type}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" display="block" color="text.secondary" gutterBottom>
                  Commitment
                </Typography>
                <Typography variant="body2">
                  {application.opportunity.commitment_hours} hours
                </Typography>
              </Box>
            </Box>
          </Paper>
        ))}
      </Stack>
    </>
  );
}; 